import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardTitle,
  Checkbox,
  FormField,
  Input,
  Select,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@invested/ui';
import * as api from '../../services/api';
import { ApiError } from '../../services/api';
import type { Participant } from '../../types';
import { PARTICIPANT_STATUS_LABEL, PARTICIPANT_STATUS_TONE, SAMPLE_STATUS_LABEL, WITHDRAWAL_SCOPE_LABEL } from './statusLabels';

const STATUS_OPTIONS = Object.entries(PARTICIPANT_STATUS_LABEL).map(([value, label]) => ({ value, label }));

function ConsentForm({ participantId, onSaved }: { participantId: string; onSaved: () => void }) {
  const [consentVersion, setConsentVersion] = useState('1.0');
  const [signedDate, setSignedDate] = useState('');
  const [commercialModelConsent, setCommercialModelConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await api.createConsent({ participantId, consentVersion, signedDate, commercialModelConsent });
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke registrere samtykke.');
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Samtykkeversjon" htmlFor="consentVersion" required>
          <Input id="consentVersion" required value={consentVersion} onChange={(e) => setConsentVersion(e.target.value)} />
        </FormField>
        <FormField label="Dato signert" htmlFor="signedDate" required>
          <Input id="signedDate" type="date" required value={signedDate} onChange={(e) => setSignedDate(e.target.value)} />
        </FormField>
      </div>
      <Checkbox
        label="§14 — deltaker samtykker til at avidentifiserte data kan brukes i kommersiell kalibreringsmodell-utvikling"
        description="Eget, frivillig avkrysningspunkt — jf. DPIA avsnitt 6.1. La stå ukrysset dersom deltaker ikke har samtykket til dette."
        checked={commercialModelConsent}
        onChange={(e) => setCommercialModelConsent(e.target.checked)}
      />
      {error && (
        <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {error}
        </p>
      )}
      <Button type="submit" disabled={pending}>
        {pending ? 'Registrerer…' : 'Registrer samtykke'}
      </Button>
    </form>
  );
}

function ReferenceTestForm({ participantId, onSaved }: { participantId: string; onSaved: () => void }) {
  const [vendor, setVendor] = useState('TruDiagnostic');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await api.createReferenceTest({ participantId, vendor });
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke registrere referansetest.');
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <FormField label="Leverandør" htmlFor="vendor" className="min-w-[220px]">
        <Input id="vendor" required value={vendor} onChange={(e) => setVendor(e.target.value)} />
      </FormField>
      <Button type="submit" disabled={pending} size="sm">
        {pending ? 'Bestiller…' : 'Bestill referansetest'}
      </Button>
      {error && <p className="text-sm text-red-700">{error}</p>}
    </form>
  );
}

export function ParticipantDetailPage() {
  const { id } = useParams();
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [error, setError] = useState(false);
  const [statusPending, setStatusPending] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    api
      .getParticipant(id)
      .then(setParticipant)
      .catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function changeStatus(status: string) {
    if (!id) return;
    setStatusPending(true);
    try {
      await api.updateParticipant(id, { status: status as Participant['status'] });
      load();
    } finally {
      setStatusPending(false);
    }
  }

  if (error) return <p className="text-sm text-red-700">Fikk ikke lastet deltaker.</p>;
  if (!participant) return <p className="text-sm text-black/50">Laster…</p>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <CardTitle as="h1">{participant.pseudonymId}</CardTitle>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <StatusPill tone={PARTICIPANT_STATUS_TONE[participant.status]}>
              {PARTICIPANT_STATUS_LABEL[participant.status]}
            </StatusPill>
            {participant.ageYears !== null && <Badge tone="neutral">{participant.ageYears} år</Badge>}
            {participant.sex && <Badge tone="neutral">{participant.sex === 'FEMALE' ? 'Kvinne' : 'Mann'}</Badge>}
          </div>
        </div>
        <FormField label="Endre status" htmlFor="statusSelect" className="min-w-[200px]">
          <Select
            id="statusSelect"
            value={participant.status}
            disabled={statusPending}
            onChange={(e) => changeStatus(e.target.value)}
            options={STATUS_OPTIONS}
          />
        </FormField>
      </div>

      <Card size="lg">
        <CardTitle as="h2">Samtykke</CardTitle>
        <div className="mt-4 space-y-4">
          {(participant.consents ?? []).map((consent) => (
            <div key={consent.id} className="rounded-xl border border-black/10 p-4 text-sm">
              <p>
                Versjon {consent.consentVersion} — signert {consent.signedDate.slice(0, 10)}
              </p>
              <p className="mt-1 text-black/60">
                §14 kommersiell modellutvikling: {consent.commercialModelConsent ? 'Ja' : 'Nei'}
              </p>
              {consent.withdrawalDate && (
                <p className="mt-1 text-red-700">
                  Trukket {consent.withdrawalDate.slice(0, 10)} —{' '}
                  {consent.withdrawalScope ? WITHDRAWAL_SCOPE_LABEL[consent.withdrawalScope] : 'ingen presisert scope'}
                </p>
              )}
            </div>
          ))}
          <ConsentForm participantId={participant.id} onSaved={load} />
        </div>
      </Card>

      <Card size="lg">
        <CardTitle as="h2">Prøver</CardTitle>
        <div className="mt-4">
          {(participant.samples ?? []).length === 0 ? (
            <p className="text-sm text-black/50">Ingen prøver registrert ennå.</p>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Kryo-ID</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Tatt</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Fryserposisjon</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(participant.samples ?? []).map((sample) => (
                  <TableRow key={sample.id}>
                    <TableCell>{sample.cryoLabelId}</TableCell>
                    <TableCell>{sample.type === 'VENOUS_EDTA' ? 'Venøs (EDTA)' : 'DBS-kort'}</TableCell>
                    <TableCell>{sample.takenAt.slice(0, 10)}</TableCell>
                    <TableCell>{SAMPLE_STATUS_LABEL[sample.status]}</TableCell>
                    <TableCell>
                      {[sample.freezerBox, sample.freezerRow, sample.freezerColumn].filter(Boolean).join(' / ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="mt-3 text-xs text-black/45">
            Bruk «Prøveregistrering» i menyen for å registrere en ny prøve for denne deltakeren.
          </p>
        </div>
      </Card>

      <Card size="lg">
        <CardTitle as="h2">Referansetest</CardTitle>
        <div className="mt-4 space-y-4">
          {(participant.referenceTests ?? []).map((test) => (
            <div key={test.id} className="rounded-xl border border-black/10 p-4 text-sm">
              <p>
                {test.vendor} — {test.status === 'RECEIVED' ? 'mottatt' : 'bestilt'}
                {test.reportVersion ? ` (rapport ${test.reportVersion})` : ''}
              </p>
            </div>
          ))}
          <ReferenceTestForm participantId={participant.id} onSaved={load} />
        </div>
      </Card>
    </div>
  );
}
